import './WorkExperience.css'

import { SOMETHING_WENT_WRONG_MESSAGE, TRY_UPDATE_CHANGES_AGAIN_MESSAGE, UPDATE_SUCCESS_MESSAGE } from '../../common/constants';
import { getUserInfoAndToken, parseAndFormatDateToMonYear, parseAndFormatMMYYYYDate, parseAndFormatMonYearDate } from '../../common/helper';
import { useEffect, useRef, useState } from 'react';

import Button from '../../components/button/Button';
import ConfirmDeletionModal from './ConfirmDeletionModal';
import Loader from '../../components/loader/Loader';
import Toast from '../../components/toast/Toast';
import WorkExperienceForm from './WorkExperienceForm';
import analytics from '../../firebase';
import { logEvent } from "firebase/analytics";
import { updateWorkExperience } from '../../services/ProfileService';

const WorkExperience = ({ user, handleScroll }) => {
    const [newForm, setNewForm] = useState(false);
    const [jobTitle, setJobTitle] = useState('');
    const [jobTitleErrorMessage, setJobTitleErrorMessage] = useState('');
    const [companyName, setCompanyName] = useState('');
    const [companyNameErrorMessage, setCompanyNameErrorMessage] = useState('');
    const [fromWorkPeriod, setFromWorkPeriod] = useState('');
    const [fromWorkPeriodErrorMessage, setFromWorkPeriodErrorMessage] = useState('');
    const [toWorkPeriod, setToWorkPeriod] = useState('');
    const [toWorkPeriodErrorMessage, setToWorkPeriodErrorMessage] = useState('');
    const [currentlyWorkHere, setCurrentlyWorkHere] = useState(false);
    const [keyJobResponsibilities, setKeyJobResponsibilities] = useState('');
    const [workExperiences, setWorkExperiences] = useState([]);
    const [editIndex, setEditIndex] = useState(-1);
    const [backDateErrorMessage, setBackDateErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessageHeader, setErrorMessageHeader] = useState('');
    const [errorMessageDescription, setErrorMessageDescription] = useState('');
    const [successMessageHeader, setSuccessMessageHeader] = useState('');
    const [successMessageDescription, setSuccessMessageDescription] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [deleteIndex, setDeleteIndex] = useState(0);

    const workExperienceLisRef = useRef([]);

    useEffect(() => {
        if (user?.exp) {
            const existingWorkExperiences = transformWorkExperienceForDisplay(user?.exp);
            setWorkExperiences(existingWorkExperiences);
        }
    }, [user])

    const resetErrors = () => {
        setJobTitleErrorMessage('');
        setCompanyNameErrorMessage('');
        setFromWorkPeriodErrorMessage('');
        setToWorkPeriodErrorMessage('');
        setBackDateErrorMessage('');
    }

    const resetFieldValues = () => {
        setJobTitle('');
        setCompanyName('');
        setFromWorkPeriod('');
        setToWorkPeriod('');
        setCurrentlyWorkHere(false);
        setKeyJobResponsibilities('');
    }

    const handleCancelClick = () => {
        handleScroll();
        setNewForm(false);
        resetErrors();
        resetFieldValues();
    }

    const validateForm = () => {
        if (!jobTitle) {
            setJobTitleErrorMessage('Please specify your job position.');
        }
        if (!companyName) {
            setCompanyNameErrorMessage('Please specify the company name.');
        }
        if (!fromWorkPeriod) {
            setFromWorkPeriodErrorMessage('Please specify the start date.');
        }
        if (!currentlyWorkHere && !toWorkPeriod) {
            setToWorkPeriodErrorMessage('Please specify the end date.');
        }
    }

    const invalidDates = (startDate, endDate) => {
        if (startDate && endDate) {
            return new Date(startDate) >= new Date(endDate)
        }
        return false
    }

    const resetSuccessMessages = () => {
        setSuccessMessageHeader('');
        setSuccessMessageDescription('');
    }

    const resetErrorMessages = () => {
        setErrorMessageHeader('');
        setErrorMessageDescription('');
    }

    const handleSaveClick = async () => {
        resetErrorMessages();
        validateForm();

        const invalid = invalidDates(fromWorkPeriod, toWorkPeriod);
        if (!currentlyWorkHere && invalid) {
            setBackDateErrorMessage('Start date cannot be later than end date.');
            return
        }

        if (keyJobResponsibilities.length > 600) {
            setErrorMessageHeader('Update error');
            setErrorMessageDescription('Please keep Key job responsibilities to under 600 characters');
            return
        }

        if (jobTitle && companyName && fromWorkPeriod && (toWorkPeriod || currentlyWorkHere)) {
            try {
                resetSuccessMessages();
                setIsLoading(true);

                let workExperiencesToUpdate = [];
                if (editIndex !== -1) { // Update the values if it's edit operation
                    workExperiencesToUpdate = workExperiences.map((we, i) => {
                        if (i === editIndex) {
                            return transformWorkExperienceForAPI({
                                title: jobTitle,
                                subTitle: companyName,
                                fromDate: (we.fromDate !== fromWorkPeriod) ? parseAndFormatDateToMonYear(fromWorkPeriod) : fromWorkPeriod,
                                toDate: currentlyWorkHere ? '' : (we.toDate !== toWorkPeriod) ? parseAndFormatDateToMonYear(toWorkPeriod) : toWorkPeriod,
                                currentlyWorkHere,
                                keyJobResponsibilities
                            })
                        }
                        return transformWorkExperienceForAPI(we);
                    })
                } else {
                    workExperiencesToUpdate = [...workExperiences, {
                        title: jobTitle,
                        subTitle: companyName,
                        fromDate: fromWorkPeriod,
                        toDate: currentlyWorkHere ? '' : toWorkPeriod,
                        currentlyWorkHere,
                        keyJobResponsibilities
                    }].map((workExperience) => transformWorkExperienceForAPI(workExperience));
                }

                const { userId, username, token } = getUserInfoAndToken();
                const response = await updateWorkExperience(userId, username, token, workExperiencesToUpdate);
                if (response?.code === '00') {
                    logEvent(analytics, 'profile_work_experience_updated', { 'userid': userId, 'job_title': jobTitle, 'company_name': companyName });
                    const experiences = response?.data?.exp;
                    setWorkExperiences(transformWorkExperienceForDisplay(experiences));
                    setEditIndex(-1);

                    setNewForm(false);
                    resetErrors();
                    resetFieldValues();
                    resetErrorMessages();
                    setSuccessMessageHeader(UPDATE_SUCCESS_MESSAGE);
                    setSuccessMessageDescription('Your changes to Work experience has been saved!');
                } else {
                    const messages = response?.message;
                    if (messages?.length > 0) {
                        setErrorMessageHeader('Update error');
                        setErrorMessageDescription(messages.join('\n'));
                    }
                    setNewForm(true);
                }

                handleScroll();
                setIsLoading(false);
            } catch (error) {
                console.log(`error occurred while deleting the work experiences: ${error}`);

                setNewForm(true);
                setIsLoading(false);
                setErrorMessageHeader(SOMETHING_WENT_WRONG_MESSAGE);
                setErrorMessageDescription(TRY_UPDATE_CHANGES_AGAIN_MESSAGE);
            }
        }
    }

    const transformWorkExperienceForDisplay = (exp) => {
        return exp.map((e) => {
            return {
                title: e.title,
                subTitle: e.coym,
                fromDate: e.start ? parseAndFormatMMYYYYDate(e.start) : '',
                toDate: e.end ? parseAndFormatMMYYYYDate(e.end) : '',
                currentlyWorkHere: (e.ispresent === '1'),
                keyJobResponsibilities: e.dsc
            }
        });
    };

    const transformWorkExperienceForAPI = (workExperience) => {
        return {
            title: workExperience.title,
            coym: workExperience.subTitle,
            start: workExperience.fromDate ? parseAndFormatMonYearDate(workExperience.fromDate) : '',
            end: workExperience.toDate ? parseAndFormatMonYearDate(workExperience.toDate) : '',
            present: workExperience.currentlyWorkHere ? '1' : '',
            desc: workExperience.keyJobResponsibilities
        }
    };

    const handleDeleteClick = async () => {
        try {
            resetErrorMessages();
            setIsLoading(true);

            const workExperiencesToUpdate = workExperiences.filter((_, i) => i !== deleteIndex)
                .map((workExperience) => transformWorkExperienceForAPI(workExperience));

            const { userId, username, token } = getUserInfoAndToken();
            const response = await updateWorkExperience(userId, username, token, workExperiencesToUpdate);
            if (response?.code === '00') {
                setShowModal(false)
                if (response?.data) {
                    const experiences = response?.data?.exp;
                    if (experiences) {
                        setWorkExperiences(transformWorkExperienceForDisplay(experiences));
                    } else {
                        setWorkExperiences([]);
                    }
                } else {
                    setWorkExperiences([]);
                }

                setSuccessMessageHeader(UPDATE_SUCCESS_MESSAGE);
                setSuccessMessageDescription('Your changes to Work experience has been saved!');
            } else {
                setShowModal(false)
                const messages = response?.message;
                if (messages?.length > 0) {
                    setErrorMessageHeader('Update error');
                    setErrorMessageDescription(messages.join('\n'));
                }
            }

            setNewForm(false);
            setIsLoading(false);
        } catch (error) {
            console.log(`error occurred while deleting the work experiences: ${error}`);

            setIsLoading(false);
            setErrorMessageHeader(SOMETHING_WENT_WRONG_MESSAGE);
            setErrorMessageDescription(TRY_UPDATE_CHANGES_AGAIN_MESSAGE);
        }
    }

    const handleDelete = (index) => {
        setShowModal(true)
        setDeleteIndex(index)
    }

    const handleEditClick = (index) => {
        setEditIndex(index);
        setNewForm(true);

        setJobTitle(workExperiences[index].title);
        setCompanyName(workExperiences[index].subTitle);
        setFromWorkPeriod(workExperiences[index].fromDate);
        setToWorkPeriod(workExperiences[index].currentlyWorkHere ? '' : workExperiences[index].toDate);
        setCurrentlyWorkHere(workExperiences[index].currentlyWorkHere);
        setKeyJobResponsibilities(workExperiences[index].keyJobResponsibilities);
    }

    const generateCSS = () => {
        try {
            const styleTag = document.createElement('style');
            let css = '';

            // Calculate each li element content height
            const heights = [];
            workExperienceLisRef.current.forEach((li) => {
                if (li) {
                    const titleHeight = li.querySelector('.fb-list-title').clientHeight;
                    const subTitleHeight = li.querySelector('.fb-list-subtitle').clientHeight;
                    const dateRangeHeight = li.querySelector('.fb-list-date-range').clientHeight;
                    let totalHeight = (titleHeight + subTitleHeight + dateRangeHeight);
                    heights.push(totalHeight);
                }
            });

            // Adjust vertical line height as per next li element
            heights.forEach((_, index) => {
                if (index + 1 !== heights.length) {
                    let current = heights[index];
                    let next = heights[index + 1];
                    let total = current + (next / 2) - (current / 2) + 10;
                    css += `.fb-list-container li:nth-child(${index + 1})::before { height: ${total}px; }`;
                }
            })

            styleTag.innerHTML = css;
            document.head.appendChild(styleTag);
        } catch (error) {
            console.log(`error occurred while generating css: ${error}`)
        }
    };

    const addButtonClicked = () => {
        setNewForm(true)
        logEvent(analytics, 'profile_work_experience_initiated');
    }

    useEffect(() => {
        generateCSS();
    }, [workExperiences]);

    return (
        <>
            {
                (successMessageHeader && successMessageDescription) && <Toast
                    type='success'
                    header={successMessageHeader}
                    message={successMessageDescription}
                    onClose={resetSuccessMessages}
                />
            }
            {
                (errorMessageHeader && errorMessageDescription) && <Toast
                    type='error'
                    header={errorMessageHeader}
                    message={errorMessageDescription}
                    onClose={resetErrorMessages}
                />
            }
            <ConfirmDeletionModal showModal={showModal} setShowModal={setShowModal} handleConfirm={handleDeleteClick} />
            {isLoading && <Loader></Loader>}
            <>
                <div className='fb-header-container'>
                    <div className='fb-header-btn-wrapper'>
                        <p className='fb-section-title'>Work experience</p>
                        <Button className='fb-add-btn' onClick={addButtonClicked}>
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M7 0.125C7.34518 0.125 7.625 0.404822 7.625 0.75V6.375H13.25C13.5952 6.375 13.875 6.65482 13.875 7C13.875 7.34518 13.5952 7.625 13.25 7.625H7.625V13.25C7.625 13.5952 7.34518 13.875 7 13.875C6.65482 13.875 6.375 13.5952 6.375 13.25V7.625H0.75C0.404822 7.625 0.125 7.34518 0.125 7C0.125 6.65482 0.404822 6.375 0.75 6.375H6.375V0.75C6.375 0.404822 6.65482 0.125 7 0.125Z" fill="#D31145" />
                            </svg>
                            <p className='fb-add-btn-text'>Add</p>
                        </Button>
                    </div>
                    <p className='fb-description'>Let the employers know about your work experience!</p>
                </div>
                {
                    workExperiences.length > 0 && <div className='fb-list-container'>
                        <ul>
                            {
                                workExperiences.map((value, index) => (
                                    <li key={index} ref={el => workExperienceLisRef.current[index] = el}>
                                        <svg className='list-icon' width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <circle cx="6" cy="6" r="5.25" stroke="#D31145" strokeWidth="1.5" />
                                        </svg>
                                        <div className='fb-list-contents-wrapper'>
                                            <label className='fb-list-title'>{value.title}</label>
                                            <label className='fb-list-subtitle'>{value.subTitle}</label>
                                            <label className='fb-list-date-range'>{`${value.fromDate} - ${value.currentlyWorkHere ? 'present' : value.toDate}`}</label>
                                        </div>
                                        <div className='fb-list-actions-container'>
                                            <Button className='fb-list-btn-wrapper' onClick={() => handleDelete(index)}>
                                                <svg width="18" height="22" viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M9.00012 2C8.45247 2 7.90689 2.00876 7.36352 2.02614C6.6089 2.05028 6.00012 2.68393 6.00012 3.47819V3.59082C6.99249 3.53056 7.99278 3.5 9.00012 3.5C10.0075 3.5 11.0078 3.53056 12.0001 3.59082V3.47819C12.0001 2.68393 11.3913 2.05028 10.6367 2.02614C10.0934 2.00876 9.54778 2 9.00012 2ZM13.5001 3.70498V3.47819C13.5001 1.91371 12.2876 0.578183 10.6847 0.526907C10.1253 0.509014 9.56377 0.5 9.00012 0.5C8.43648 0.5 7.87491 0.509014 7.31556 0.526907C5.71265 0.578183 4.50012 1.91371 4.50012 3.47819V3.70498C3.5454 3.79237 2.59858 3.90731 1.66048 4.04898C1.31315 4.10143 0.96701 4.15755 0.622112 4.2173C0.213976 4.28799 -0.0595705 4.67617 0.011128 5.0843C0.0818265 5.49244 0.469998 5.76598 0.878134 5.69529C0.947738 5.68323 1.0174 5.67132 1.0871 5.65957L2.09253 18.7301C2.21276 20.2931 3.51608 21.5 5.08369 21.5H12.9166C14.4842 21.5 15.7875 20.2931 15.9077 18.7301L16.9131 5.65957C16.9829 5.67132 17.0525 5.68323 17.1221 5.69529C17.5302 5.76598 17.9184 5.49244 17.9891 5.0843C18.0598 4.67617 17.7863 4.28799 17.3781 4.2173C17.0332 4.15755 16.6871 4.10143 16.3398 4.04898C15.4017 3.90731 14.4549 3.79237 13.5001 3.70498ZM15.4261 5.43321C14.5228 5.3104 13.6113 5.21311 12.6923 5.14209C11.4741 5.04796 10.2428 5 9.00012 5C7.75746 5 6.52614 5.04796 5.30791 5.14209C4.38898 5.21311 3.47747 5.3104 2.57412 5.43321L3.58811 18.615C3.64823 19.3965 4.29989 20 5.08369 20H12.9166C13.7004 20 14.352 19.3965 14.4121 18.615L15.4261 5.43321ZM6.23091 7.25055C6.64482 7.23463 6.99326 7.55727 7.00918 7.97118L7.35534 16.9712C7.37126 17.3851 7.04862 17.7335 6.63472 17.7494C6.22081 17.7654 5.87237 17.4427 5.85645 17.0288L5.51029 8.02882C5.49437 7.61492 5.81701 7.26647 6.23091 7.25055ZM11.7693 7.25055C12.1832 7.26647 12.5059 7.61492 12.49 8.02882L12.1438 17.0288C12.1279 17.4427 11.7794 17.7654 11.3655 17.7494C10.9516 17.7335 10.629 17.3851 10.6449 16.9712L10.9911 7.97118C11.007 7.55727 11.3554 7.23463 11.7693 7.25055Z" fill="#B91C1C" />
                                                </svg>
                                            </Button>
                                            <Button className='fb-list-btn-wrapper' onClick={() => handleEditClick(index)}>
                                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M21.7312 2.26884C20.706 1.24372 19.044 1.24372 18.0189 2.26884L16.8617 3.426L20.574 7.13831L21.7312 5.98116C22.7563 4.95603 22.7563 3.29397 21.7312 2.26884Z" fill="#18181B" />
                                                    <path d="M19.5134 8.19897L15.801 4.48666L3.65021 16.6375C3.03342 17.2543 2.58003 18.015 2.33101 18.851L1.53123 21.5359C1.45261 21.7998 1.52496 22.0856 1.71969 22.2803C1.91442 22.4751 2.2002 22.5474 2.46413 22.4688L5.14902 21.669C5.98499 21.42 6.74574 20.9666 7.36253 20.3498L19.5134 8.19897Z" fill="#18181B" />
                                                </svg>
                                            </Button>
                                        </div>
                                    </li>
                                ))}
                        </ul>
                    </div>
                }
                {
                    newForm && <WorkExperienceForm
                        jobTitle={jobTitle}
                        setJobTitle={setJobTitle}
                        companyName={companyName}
                        setCompanyName={setCompanyName}
                        fromWorkPeriod={fromWorkPeriod}
                        setFromWorkPeriod={setFromWorkPeriod}
                        toWorkPeriod={toWorkPeriod}
                        setToWorkPeriod={setToWorkPeriod}
                        currentlyWorkHere={currentlyWorkHere}
                        setCurrentlyWorkHere={setCurrentlyWorkHere}
                        keyJobResponsibilities={keyJobResponsibilities}
                        setKeyJobResponsibilities={setKeyJobResponsibilities}

                        jobTitleErrorMessage={jobTitleErrorMessage}
                        companyNameErrorMessage={companyNameErrorMessage}
                        fromWorkPeriodErrorMessage={fromWorkPeriodErrorMessage}
                        toWorkPeriodErrorMessage={toWorkPeriodErrorMessage}
                        setJobTitleErrorMessage={setJobTitleErrorMessage}
                        setCompanyNameErrorMessage={setCompanyNameErrorMessage}
                        setFromWorkPeriodErrorMessage={setFromWorkPeriodErrorMessage}
                        setToWorkPeriodErrorMessage={setToWorkPeriodErrorMessage}
                        backDateErrorMessage={backDateErrorMessage}
                        setBackDateErrorMessage={setBackDateErrorMessage}

                        handleCancelClick={handleCancelClick}
                        handleSaveClick={handleSaveClick}
                    />
                }
            </>
        </>
    )
}

export default WorkExperience;
